import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Segmented, Select, Spin, message } from 'antd';
import {
  EditOutlined,
  FileAddOutlined,
  FileOutlined,
  FileTextOutlined,
  MailOutlined,
  PictureOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import type { Trip } from '../../types/client';
import { notesAttachmentsEn } from '../../content/trips.en';
import { downloadFile, uploadDeviceSyncFile } from '../../services/filesService';
import { fetchInboundPendingCount } from '../../services/inboundEmailService';
import InboundConfirmationsInbox from '../InboundConfirmationsInbox/InboundConfirmationsInbox';
import {
  type CollectedNote,
  type NotesAttachmentScope,
  addNoteItem,
  attachFileToHolder,
  collectAttachments,
  collectUserNotes,
  deleteNoteItem,
  fileIdForAttachment,
  formatNoteDateLabel,
  removeAttachment,
  updateNoteItem,
} from '../../utils/notesAttachments';
import styles from './NotesAttachmentsCard.module.css';

const GRID_MAX = 6;

export interface NotesAttachmentsCardProps {
  trip: Trip;
  /** Placement day (Day View). When omitted, Trip Detail trip-wide add is enabled. */
  dayDate?: string;
  cityVisitId?: string;
  /** Optional city filter list for trip-level viewing */
  cityOptions?: { id: string; name: string }[];
  defaultExpanded?: boolean;
  /** Day View Expand/Collapse — syncs card open state when the footer toggles. */
  expandAll?: boolean;
  /** When false, hide Add Note / Attach (view-only shared trips). */
  canEdit?: boolean;
  onChangeTrip: (updates: Partial<Omit<Trip, 'id'>>) => void;
}

/** Trip Detail filter: All | Trip-wide | city id */
type TripFilterValue = 'all' | 'tripWide' | string;

function emptyMessage(
  scope: NotesAttachmentScope,
  hasDay: boolean
): string {
  if (scope === 'tripWide') return notesAttachmentsEn.emptyTripWide;
  if (!hasDay) return notesAttachmentsEn.emptyAll;
  if (scope === 'today') return notesAttachmentsEn.emptyToday;
  if (scope === 'city') return notesAttachmentsEn.emptyCity;
  if (scope === 'trip') return notesAttachmentsEn.emptyTrip;
  return notesAttachmentsEn.emptyAll;
}

const AttachmentThumb: React.FC<{
  trip: Trip;
  attachmentId: string;
  fileName: string;
  mimeType: string;
  onOpen: () => void;
}> = ({ trip, attachmentId, fileName, mimeType, onOpen }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isImage = mimeType.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    const fileId = fileIdForAttachment(trip, attachmentId);
    if (!fileId) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void downloadFile(fileId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        /* keep icon fallback */
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [trip, attachmentId, isImage]);

  return (
    <button
      type="button"
      className={styles.thumb}
      onClick={onOpen}
      title={fileName}
    >
      {previewUrl ? (
        <img src={previewUrl} alt="" className={styles.thumbImage} />
      ) : isImage ? (
        <PictureOutlined className={styles.thumbIcon} />
      ) : (
        <FileOutlined className={styles.thumbIcon} />
      )}
      <span className={styles.thumbName}>{fileName}</span>
    </button>
  );
};

const NotesAttachmentsCard: React.FC<NotesAttachmentsCardProps> = ({
  trip,
  dayDate,
  cityVisitId,
  cityOptions = [],
  defaultExpanded = false,
  expandAll,
  canEdit = true,
  onChangeTrip,
}) => {
  const isTripPlacement = !dayDate;
  const canAdd = canEdit;

  const [expanded, setExpanded] = useState(() => {
    if (defaultExpanded) return true;
    if (!dayDate) {
      const n = collectUserNotes(trip, 'allTrip', {}).length;
      const a = collectAttachments(trip, 'allTrip', {}).length;
      return n + a > 0;
    }
    return false;
  });
  const expandAllRef = useRef(expandAll);
  const [scope, setScope] = useState<NotesAttachmentScope>(
    isTripPlacement ? 'allTrip' : 'today'
  );
  const [tripFilter, setTripFilter] = useState<TripFilterValue>('all');
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<CollectedNote | null>(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [processing, setProcessing] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expandAll === undefined) return;
    if (expandAll !== expandAllRef.current) {
      expandAllRef.current = expandAll;
      setExpanded(expandAll);
    }
  }, [expandAll]);

  useEffect(() => {
    let cancelled = false;
    void fetchInboundPendingCount().then((n) => {
      if (!cancelled) setPendingCount(n);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    void fetchInboundPendingCount().then((n) => {
      if (!cancelled) setPendingCount(n);
    });
    return () => {
      cancelled = true;
    };
  }, [expanded]);

  const collectCtx = useMemo(() => {
    if (isTripPlacement) {
      if (tripFilter === 'all' || tripFilter === 'tripWide') return {};
      return { cityVisitId: tripFilter };
    }
    return { dayDate, cityVisitId };
  }, [isTripPlacement, tripFilter, dayDate, cityVisitId]);

  const effectiveScope: NotesAttachmentScope = isTripPlacement
    ? tripFilter === 'tripWide'
      ? 'tripWide'
      : tripFilter === 'all'
        ? 'allTrip'
        : 'city'
    : scope;

  const notes = useMemo(
    () => collectUserNotes(trip, effectiveScope, collectCtx),
    [trip, effectiveScope, collectCtx]
  );

  const attachments = useMemo(
    () => collectAttachments(trip, effectiveScope, collectCtx),
    [trip, effectiveScope, collectCtx]
  );

  const totalCount = notes.length + attachments.length;
  const grid = attachments.slice(0, GRID_MAX);
  const showDateSubtitle =
    isTripPlacement || effectiveScope === 'city' || effectiveScope === 'trip';

  const persistTrip = (next: Trip): void => {
    // Schema 1.4: omit tripItems when undefined so day-scoped saves do not wipe
    // trip-wide items the in-memory trip never hydrated.
    const updates: Partial<Omit<Trip, 'id'>> = {
      itineraryDays: next.itineraryDays,
      attachmentRefs: next.attachmentRefs,
      attachmentFileIds: next.attachmentFileIds,
    };
    if (next.tripItems !== undefined) {
      updates.tripItems = next.tripItems;
    }
    onChangeTrip(updates);
  };

  const openAddNoteModal = (): void => {
    setEditingNote(null);
    setNoteTitle('');
    setNoteBody('');
    setNoteModalOpen(true);
  };

  const openEditNoteModal = (note: CollectedNote): void => {
    setEditingNote(note);
    setNoteTitle(note.legacyDayNotes ? '' : note.title);
    setNoteBody(note.body);
    setNoteModalOpen(true);
  };

  const saveNote = (): void => {
    const title = noteTitle.trim();
    if (!title) {
      message.error(notesAttachmentsEn.titleRequired);
      return;
    }
    if (editingNote) {
      const next = updateNoteItem(trip, {
        noteId: editingNote.id,
        title,
        body: noteBody,
        legacyDayDate: editingNote.legacyDayNotes ? editingNote.dayDate : undefined,
        legacyCityId: editingNote.legacyDayNotes ? editingNote.cityId : undefined,
      });
      persistTrip(next);
    } else if (isTripPlacement) {
      const next = addNoteItem(trip, {
        tripWide: true,
        title,
        body: noteBody,
      });
      persistTrip(next);
      setExpanded(true);
      if (tripFilter === 'all') {
        /* keep All */
      } else {
        setTripFilter('tripWide');
      }
    } else if (dayDate) {
      const next = addNoteItem(trip, {
        dayDate,
        cityId: cityVisitId,
        title,
        body: noteBody,
      });
      persistTrip(next);
      setExpanded(true);
      setScope('today');
    }
    setNoteModalOpen(false);
  };

  const handleDeleteNote = (): void => {
    if (!editingNote) return;
    persistTrip(deleteNoteItem(trip, editingNote.id));
    setNoteModalOpen(false);
  };

  const handleFiles = async (files: FileList | null, imagesOnly: boolean): Promise<void> => {
    if (!files?.length) return;
    const selected = Array.from(files);
    setProcessing(true);
    try {
      let next = trip;
      let attached = 0;
      const attachTripWide =
        isTripPlacement &&
        (tripFilter === 'all' || tripFilter === 'tripWide' || !tripFilter);
      for (const file of selected) {
        const mime = file.type || 'application/octet-stream';
        if (imagesOnly && !mime.startsWith('image/')) {
          message.error(notesAttachmentsEn.uploadFailed);
          continue;
        }
        const fileId = await uploadDeviceSyncFile(file, mime, file.name);
        if (attachTripWide || isTripPlacement) {
          next = attachFileToHolder(next, {
            tripWide: true,
            fileId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: mime,
          });
        } else if (dayDate) {
          next = attachFileToHolder(next, {
            dayDate,
            cityId: cityVisitId,
            fileId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: mime,
          });
        } else {
          next = attachFileToHolder(next, {
            tripWide: true,
            fileId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: mime,
          });
        }
        attached += 1;
      }
      if (attached > 0) {
        persistTrip(next);
        setExpanded(true);
        if (!isTripPlacement) {
          setScope('today');
        } else if (tripFilter !== 'all' && tripFilter !== 'tripWide') {
          // Upload is always trip-wide on TDV — show Trip-wide so the file appears.
          setTripFilter('tripWide');
        }
      }
    } catch {
      message.error(notesAttachmentsEn.uploadFailed);
    } finally {
      setProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openAttachment = async (attachmentId: string): Promise<void> => {
    const fileId = fileIdForAttachment(trip, attachmentId);
    if (!fileId) {
      message.error(notesAttachmentsEn.downloadFailed);
      return;
    }
    try {
      const blob = await downloadFile(fileId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      message.error(notesAttachmentsEn.downloadFailed);
    }
  };

  const deleteAttachment = (attachmentId: string): void => {
    persistTrip(removeAttachment(trip, attachmentId));
  };

  const inboxAria =
    pendingCount > 0
      ? `${notesAttachmentsEn.openInbox}. ${notesAttachmentsEn.pendingInbox(pendingCount)}`
      : notesAttachmentsEn.openInbox;

  return (
    <div className={styles.card} data-testid="notes-attachments-card">
      <button
        type="button"
        className={styles.headerButton}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <FileTextOutlined className={styles.headerIcon} aria-hidden />
        <span className={styles.title}>{notesAttachmentsEn.title}</span>
        {totalCount > 0 ? <span className={styles.badge}>{totalCount}</span> : null}
        <span className={styles.chevron} aria-hidden>
          {expanded ? '▴' : '▾'}
        </span>
      </button>

      {expanded ? (
        <div className={styles.body}>
          {isTripPlacement ? (
            <Select
              className={styles.cityFilter}
              value={tripFilter}
              onChange={(v) => setTripFilter((v ? String(v) : 'all') as TripFilterValue)}
              options={[
                { value: 'all', label: notesAttachmentsEn.allTrip },
                { value: 'tripWide', label: notesAttachmentsEn.scopeTripWide },
                ...cityOptions.map((c) => ({ value: c.id, label: c.name })),
              ]}
              aria-label={notesAttachmentsEn.cityFilterLabel}
              data-testid="notes-city-filter"
            />
          ) : null}

          {!isTripPlacement ? (
            <Segmented
              block
              className={styles.scope}
              value={scope}
              onChange={(v) => setScope(v as NotesAttachmentScope)}
              options={[
                { label: notesAttachmentsEn.scopeToday, value: 'today' },
                { label: notesAttachmentsEn.scopeCity, value: 'city' },
                { label: notesAttachmentsEn.scopeTrip, value: 'trip' },
              ]}
              data-testid="notes-day-scope"
            />
          ) : null}

          {notes.length > 0 ? (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{notesAttachmentsEn.notesSection}</div>
              <ul className={styles.notesList}>
                {notes.map((n) => (
                  <li key={n.id} className={styles.noteRow}>
                    <div className={styles.noteMain}>
                      <div className={styles.noteLine}>
                        {n.title ? (
                          <>
                            <span className={styles.noteTitle}>{n.title}</span>
                            {n.body ? (
                              <>
                                <span className={styles.noteDash}> – </span>
                                <span className={styles.noteBody}>{n.body}</span>
                              </>
                            ) : null}
                          </>
                        ) : (
                          <span className={styles.noteBody}>{n.body}</span>
                        )}
                      </div>
                      {showDateSubtitle ? (
                        <div className={styles.noteDate}>{formatNoteDateLabel(n.dayDate)}</div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className={styles.noteEdit}
                      aria-label={notesAttachmentsEn.editNoteTitle}
                      onClick={() => openEditNoteModal(n)}
                    >
                      <EditOutlined />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {attachments.length > 0 ? (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {notesAttachmentsEn.attachmentsSection}
              </div>
              <div className={styles.grid}>
                {grid.map((c) => (
                  <AttachmentThumb
                    key={c.attachment.id}
                    trip={trip}
                    attachmentId={c.attachment.id}
                    fileName={c.attachment.fileName}
                    mimeType={c.attachment.mimeType}
                    onOpen={() => openAttachment(c.attachment.id)}
                  />
                ))}
              </div>
              {attachments.length > GRID_MAX ? (
                <button
                  type="button"
                  className={styles.viewAll}
                  onClick={() => setViewAllOpen(true)}
                >
                  {notesAttachmentsEn.viewAll(attachments.length)}
                </button>
              ) : null}
            </div>
          ) : null}

          {notes.length === 0 && attachments.length === 0 ? (
            <p className={styles.empty}>{emptyMessage(effectiveScope, Boolean(dayDate))}</p>
          ) : null}

          <div className={styles.actions}>
            {canAdd ? (
              <Button
                type="primary"
                size="small"
                icon={<PlusCircleOutlined />}
                onClick={openAddNoteModal}
              >
                {notesAttachmentsEn.addNote}
              </Button>
            ) : null}
            <span className={styles.actionSpacer} />
            {canAdd ? (
              <>
                <button
                  type="button"
                  className={styles.iconAction}
                  aria-label={notesAttachmentsEn.addFile}
                  disabled={processing}
                  data-testid="attachment-attach-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileAddOutlined />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className={styles.hiddenInput}
                  data-testid="attachment-file-input"
                  onChange={(e) => void handleFiles(e.target.files, false)}
                />
              </>
            ) : null}
            <button
              type="button"
              className={styles.iconAction}
              aria-label={inboxAria}
              data-testid="notes-inbox-button"
              onClick={() => setInboxOpen(true)}
            >
              <span className={styles.envelopeWrap}>
                <MailOutlined />
                {pendingCount > 0 ? (
                  <span className={styles.pendingBadge}>
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </span>
                ) : null}
              </span>
            </button>
            {processing ? <Spin size="small" /> : null}
          </div>
        </div>
      ) : null}

      <Modal
        title={editingNote ? notesAttachmentsEn.editNoteTitle : notesAttachmentsEn.addNoteTitle}
        open={noteModalOpen}
        onCancel={() => setNoteModalOpen(false)}
        destroyOnHidden
        footer={[
          editingNote ? (
            <Button key="delete" danger onClick={handleDeleteNote}>
              {notesAttachmentsEn.deleteNote}
            </Button>
          ) : null,
          <Button key="cancel" onClick={() => setNoteModalOpen(false)}>
            {notesAttachmentsEn.cancel}
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={saveNote}
            disabled={!noteTitle.trim()}
          >
            {notesAttachmentsEn.saveNote}
          </Button>,
        ]}
      >
        <div className={styles.noteForm}>
          <label className={styles.noteFieldLabel} htmlFor="note-title-input">
            {notesAttachmentsEn.noteTitleLabel}
          </label>
          <Input
            id="note-title-input"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            placeholder={notesAttachmentsEn.noteTitlePlaceholder}
            data-testid="note-title-input"
          />
          <label className={styles.noteFieldLabel} htmlFor="note-body-input">
            {notesAttachmentsEn.noteContentLabel}
          </label>
          <Input.TextArea
            id="note-body-input"
            rows={4}
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder={notesAttachmentsEn.noteContentPlaceholder}
            data-testid="note-body-input"
          />
        </div>
      </Modal>

      <Modal
        title={notesAttachmentsEn.attachmentsSection}
        open={viewAllOpen}
        onCancel={() => setViewAllOpen(false)}
        footer={null}
        destroyOnHidden
      >
        {attachments.length === 0 ? (
          <p className={styles.empty}>{notesAttachmentsEn.noAttachments}</p>
        ) : (
          <ul className={styles.fullList}>
            {attachments.map((c) => (
              <li key={c.attachment.id} className={styles.fullRow}>
                <span className={styles.fullName}>{c.attachment.fileName}</span>
                <div className={styles.fullActions}>
                  <Button size="small" onClick={() => openAttachment(c.attachment.id)}>
                    {notesAttachmentsEn.open}
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => deleteAttachment(c.attachment.id)}
                  >
                    {notesAttachmentsEn.delete}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <InboundConfirmationsInbox
        open={inboxOpen}
        onClose={() => setInboxOpen(false)}
        defaultTripId={trip.id}
        onPendingCountChange={setPendingCount}
      />
    </div>
  );
};

export default NotesAttachmentsCard;
